import { BaseApi } from '@/modules/core/base.api';
import { User } from './user';
import { Agency } from '@/modules/agency/agency';
import * as API from '@shared/common/api';
import { PaginationResult } from '@shared/common/types';

class UserApi extends BaseApi {
    public apiPath = 'user';

    public async findAll(options: object & { page: number }): Promise<PaginationResult<User>>;
    public async findAll(options?: object): Promise<User[]>;
    public async findAll(options: any = {}): Promise<User[] | PaginationResult<User>> {
        const response = await this.api.get(this.baseUrlFor(), {
            params: {
                ...options,
            },
        });

        if (response.data.items) {
            response.data.items = response.data.items.map((user: any) => {
                const userInstance = new User(user);

                if (user.agency) {
                    userInstance.agency = new Agency(user.agency);
                }

                return userInstance;
            });
        } else {
            response.data = response.data.map((user: any) => {
                const userInstance = new User(user);

                if (user.agency) {
                    userInstance.agency = new Agency(user.agency);
                }

                return userInstance;
            });
        }

        return response.data;
    }

    public async create(user: any): Promise<API.CreateUser> {
        const response = await this.api.post<API.CreateUser>(this.baseUrlFor(), user);
        return response.data;
    }

    public async update(user: any): Promise<API.UpdateUser> {
        const response = await this.api.put<API.UpdateUser>(this.baseUrlFor(user.id), user);
        return new User(response.data);
    }

    public async get(id: string): Promise<User> {
        const response = await this.api.get<API.GetUser>(this.baseUrlFor(id));
        const user = new User(response.data);

        if (user.agency) {
            user.agency = new Agency(user.agency);
        }

        return user;
    }

    public async delete(id: string): Promise<API.DeleteUser> {
        const response = await this.api.delete<API.DeleteUser>(this.baseUrlFor(id));
        return response.data;
    }

    public async getManagers(filterString: string): Promise<User[]> {
        const response = await this.api.get<API.GetManagers>(this.baseUrlFor('managers'), {
            params: {
                filterString,
            },
        });
        return response.data.map((user: any) => new User(user));
    }

    public async getCount(options: any): Promise<API.GetUserCount> {
        const response = await this.api.get<API.GetUserCount>(this.baseUrlFor('count'), {
            params: {
                ...options,
            },
        });
        return response.data;
    }


    public async isEmailAvailable(email: string): Promise<API.IsUserEmailAvailable> {
        const response = await this.api.get<API.IsUserEmailAvailable>(this.baseUrlFor('is_email_available'), {
            params: {
                email,
            },
        });

        return response.data;
    }

    public async isActivationTokenValid(token: string): Promise<API.IsActivationTokenValid> {
        const response = await this.api.get<API.IsActivationTokenValid>(this.baseUrlFor('is_activation_token_valid'), {
            params: {
                token,
            },
        });

        return response.data;
    }

    public async getUserByPasswordToken(token: string): Promise<User> {
        const response = await this.api.get<API.GetUserByPasswordToken>(this.baseUrlFor('by_password_token'), {
            params: {
                token,
            },
        });

        return new User(response.data);
    }

    public async activateAccount(data: any): Promise<API.ActivateUserAccount> {
        const response = await this.api.post<API.ActivateUserAccount>(this.baseUrlFor('activate_account'), {
            password: data.password,
        }, {
            params: {
                token: data.token,
            },
        });

        return response.data;
    }
}

export const userApi = new UserApi();
